/**
 * 權限更新服務
 * 負責管理權限更新後的選單重新渲染
 */

class PermissionUpdateService {
  constructor() {
    this.updateCallbacks = []
    this.isInitialized = false
    this.abilityWatcher = null
    this.lastAbilityRules = null
  }

  /**
   * 初始化服務
   */
  init() {
    if (this.isInitialized) return

    // 將服務掛載到全局
    if (typeof window !== 'undefined') {
      window.permissionUpdateService = this
    }

    // 啟動 ability 監聽器
    this.startAbilityWatcher()

    this.isInitialized = true
    console.log('🔧 權限更新服務已初始化')
  }

  /**
   * 啟動 ability 實例監聽器
   */
  startAbilityWatcher() {
    // 定期檢查 ability 實例的變化
    this.abilityWatcher = setInterval(() => {
      this.checkAbilityChanges()
    }, 1000) // 每秒檢查一次

    console.log('👁️ Ability 監聽器已啟動')
  }

  /**
   * 檢查 ability 實例的變化
   */
  checkAbilityChanges() {
    try {
      // 動態導入 ability 實例
      import('@/libs/acl/ability').then(({ default: ability }) => {
        if (!ability || !ability.rules) {
          return
        }

        // 將規則轉換為字符串進行比較
        const currentRules = JSON.stringify(ability.rules)

        if (this.lastAbilityRules === null) {
          // 首次初始化
          this.lastAbilityRules = currentRules
          return
        }

        if (this.lastAbilityRules !== currentRules) {
          console.log('🔄 檢測到 ability 規則變化，觸發權限更新')
          this.lastAbilityRules = currentRules
          this.triggerUpdate('ability-watcher')
        }
      }).catch(error => {
        console.warn('⚠️ 無法檢查 ability 變化:', error)
      })
    } catch (error) {
      console.warn('⚠️ Ability 監聽器錯誤:', error)
    }
  }

  /**
   * 註冊更新回調函數
   * @param {Function} callback 更新回調函數
   * @param {String} name 回調函數名稱（用於調試）
   */
  registerUpdateCallback(callback, name = 'anonymous') {
    if (typeof callback !== 'function') {
      console.warn('⚠️ 註冊的更新回調必須是函數')
      return
    }

    this.updateCallbacks.push({ callback, name })
    console.log(`📝 已註冊權限更新回調: ${name}`)
  }

  /**
   * 移除更新回調函數
   * @param {Function} callback 要移除的回調函數
   */
  unregisterUpdateCallback(callback) {
    const index = this.updateCallbacks.findIndex(item => item.callback === callback)
    if (index > -1) {
      const removed = this.updateCallbacks.splice(index, 1)[0]
      console.log(`🗑️ 已移除權限更新回調: ${removed.name}`)
    }
  }

  /**
   * 觸發所有權限更新回調
   * @param {String} source 觸發來源（用於調試）
   */
  triggerUpdate(source = 'unknown') {
    console.log(`🔄 觸發權限更新 - 來源: ${source}`)
    
    if (this.updateCallbacks.length === 0) {
      console.warn('⚠️ 沒有註冊的權限更新回調')
      return
    }

    this.updateCallbacks.forEach(({ callback, name }) => {
      try {
        callback()
        console.log(`✅ 權限更新回調執行成功: ${name}`)
      } catch (error) {
        console.error(`❌ 權限更新回調執行失敗: ${name}`, error)
      }
    })
  }

  /**
   * 延遲觸發更新（用於確保 DOM 更新完成）
   * @param {String} source 觸發來源
   * @param {Number} delay 延遲時間（毫秒）
   */
  triggerUpdateDelayed(source = 'unknown', delay = 100) {
    setTimeout(() => {
      this.triggerUpdate(source)
    }, delay)
  }

  /**
   * 獲取當前註冊的回調數量
   */
  getCallbackCount() {
    return this.updateCallbacks.length
  }

  /**
   * 清理所有回調（用於組件銷毀時）
   */
  cleanup() {
    console.log('🧹 清理權限更新服務')
    this.updateCallbacks = []

    // 清理 ability 監聽器
    if (this.abilityWatcher) {
      clearInterval(this.abilityWatcher)
      this.abilityWatcher = null
      console.log('🧹 已清理 ability 監聽器')
    }
  }
}

// 創建單例實例
const permissionUpdateService = new PermissionUpdateService()

// 自動初始化
permissionUpdateService.init()

export default permissionUpdateService
